/**
 * PIN 때문에 멈춘 쓰기 하나를 들고 있다가, 잠금이 풀리면 **한 번만** 다시 보낸다.
 *
 * ⚠ **왜 React 밖인가.** `403 pin_required` 가 오면 앱이 키패드 화면으로
 * 갈아치우고, 그 순간 그 버튼이 있던 화면은 **통째로 사라진다.** 보내려던 내용도
 * 함께 사라지므로, 사람은 PIN 을 넣은 뒤 처음부터 다시 해야 한다.
 *
 * ⚠ **한 번만이라는 것이 요점이다.** 꺼낼 때 지운다 — 남겨 두면 다음에 PIN 을
 * 풀 때 **하지도 않은 일이 또 나간다.** 승인은 되돌릴 수 없는 일이라 두 번 나가면
 * 안 된다.
 *
 * ⚠ **그 쓰기를 보낸 세션에서만 다시 나간다.** 다시 보낼 때 요청은 **그때의 토큰**을
 * 단다(client.ts). 키패드 앞에서 세션이 끝나고(401 → EA-01, 명세 2-5) 같은 탭에서
 * 다른 직원이 로그인해 PIN 을 지나면, 앞 사람의 철회·박탈·정원 승인이 확인 창 없이
 * **뒷사람 이름으로** 나간다 — 감사 기록에도 뒷사람이 한 일로 남는다. 그래서 세션이
 * 끝나면 버리고(session.tsx signOut·signIn), 기억할 때 세션 세대를 적어 두어 세대가
 * 다르면 꺼내지 않는다(로그아웃 뒤에 늦게 도착한 403 이 기억돼도 새 세션에서는 안 나간다).
 */
import { sessionGeneration } from '@/auth/token'

type Write = { run: () => Promise<void>; label: string }

let pending: (Write & { generation: number }) | null = null

/**
 * 쓰기가 PIN 에 막혔을 때 부른다. 이미 들고 있으면 **덮어쓴다** — 마지막 것 하나만.
 *
 * `generation` 은 **그 쓰기를 보낼 때**의 세션 세대다(sendWrite 가 보내기 전에 읽어 넘긴다).
 * 넘기지 않으면 지금 세대.
 */
export function rememberWrite(w: Write, generation: number = sessionGeneration()): void {
  pending = { run: w.run, label: w.label, generation }
}

/** 꺼내면서 지운다. 없거나 **다른 세션에서 기억한 것이면** null. */
export function takeWrite(): Write | null {
  const w = pending
  pending = null
  if (!w || w.generation !== sessionGeneration()) return null
  return { run: w.run, label: w.label }
}

/** 화면을 떠나거나 사람이 그만둘 때, 세션이 끝날 때. 남겨 두면 나중에 혼자 나간다. */
export function forgetWrite(): void {
  pending = null
}

/* ── 끝난 뒤 한 줄 알리기 ─────────────────────────────────────────── */

/** `warn` — 됐지만 알아야 할 것이 있다(예: 거래 중인 회원의 정지는 그 거래가 끝나면 걸린다). */
type Flash = { tone: 'ok' | 'warn' | 'stop'; text: string }
let flash: Flash | null = null
let listener: (() => void) | null = null

export function setFlash(f: Flash): void {
  flash = f
  listener?.()
}

/** 읽으면서 지운다 — 같은 알림이 두 번 보이지 않는다. */
export function takeFlash(): Flash | null {
  const f = flash
  flash = null
  return f
}

export function onFlash(fn: () => void): () => void {
  listener = fn
  return () => { if (listener === fn) listener = null }
}
